import { NavLink, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ROUTES } from '@/lib/constants';
import { useFeatureFlag } from '@/lib/hooks/useFeatureFlag';
import { quizzesApi } from '@/api/endpoints/quizzes';
import { queryKeys } from '@/lib/queryKeys';

const baseAdminLinks = [
  { to: ROUTES.ADMIN_DASHBOARD, label: 'Dashboard', end: true },
  { to: ROUTES.ADMIN_PATIENTS, label: 'Patients' },
  { to: ROUTES.ADMIN_QUEUE, label: 'Queue' },
  { to: ROUTES.AXISMATRIX_APPROVALS, label: 'AxisMatrix Approvals' },
  { to: ROUTES.ADMIN_PRACTITIONERS, label: 'Practitioners' },
  { to: ROUTES.ADMIN_USERS, label: 'EndoAxis Users' },
  { to: ROUTES.ADMIN_SYSTEM, label: 'System Data' },
];

// Links shown when the new-pattern-solver flag is OFF (legacy individual pages)
const legacyDataLinks = [
  { to: ROUTES.ADMIN_COMMENTS, label: 'Comments' },
  { to: ROUTES.ADMIN_HORMONES, label: 'Edit Hormones' },
  { to: ROUTES.ADMIN_HORMONE_LEVELS, label: 'Edit Hormone Levels' },
  { to: ROUTES.ADMIN_TRIGGERS, label: 'Comment Triggers' },
];

// Link shown when the new-pattern-solver flag is ON (consolidated page)
const algorithmManagementLink = { to: ROUTES.ADMIN_ALGORITHM_MANAGEMENT, label: 'Algorithm Management' };

// Link shown when the quiz-engine-v2 flag is ON. Independent of new-pattern-solver.
const axisMatrixManagementLink = { to: ROUTES.ADMIN_AXISMATRIX_MANAGEMENT, label: 'AxisMatrix Management' };

const remainingAdminLinks = [
  { to: ROUTES.ADMIN_SESSIONS, label: 'Observability' },
  { to: ROUTES.ADMIN_CONTENT, label: 'Content' },
  { to: ROUTES.ADMIN_PRODUCT_CATALOG, label: 'Product Catalog' },
  { to: ROUTES.ADMIN_CONTACTS, label: 'Contacts' },
  { to: ROUTES.ADMIN_CONFIG, label: 'Data Config' },
  { to: ROUTES.ADMIN_SETTINGS, label: 'Settings' },
];

const OBSERVABILITY_PREFIXES = ['/admin/sessions', '/admin/telemetry'];
const SETTINGS_PREFIXES = ['/admin/settings', '/admin/feature-flags'];
const ALGORITHM_MGMT_PREFIXES = ['/admin/algorithm-management', '/admin/formula-builder'];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-2 text-sm font-medium transition-colors ${
    isActive
      ? 'text-ea-blue underline underline-offset-4'
      : 'text-ea-black hover:text-ea-blue'
  }`;

function isActiveForPrefixes(pathname: string, prefixes: string[]) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export function Sidebar() {
  const { pathname } = useLocation();
  const { enabled: patternSolverEnabled } = useFeatureFlag('new-pattern-solver');
  const { enabled: quizEngineV2Enabled } = useFeatureFlag('quiz-engine-v2');

  const { data: pendingApprovals } = useQuery({
    queryKey: queryKeys.axisMatrix.pendingApprovals(),
    queryFn: async () => {
      const { data } = await quizzesApi.getPendingApprovals();
      return data;
    },
    staleTime: 60_000,
  });

  const pendingCount = pendingApprovals?.count ?? 0;

  const adminLinks = [
    ...baseAdminLinks,
    ...(patternSolverEnabled ? [algorithmManagementLink] : legacyDataLinks),
    ...(quizEngineV2Enabled ? [axisMatrixManagementLink] : []),
    ...remainingAdminLinks,
  ];

  return (
    <aside className="fixed left-0 top-[57px] h-[calc(100vh-57px)] w-48 bg-ea-11/50 px-4 py-6">
      <nav className="flex flex-col gap-1">
        {adminLinks.map((link) => {
          // Observability link should be active for /admin/sessions and /admin/telemetry paths
          if (link.label === 'Observability') {
            const active = isActiveForPrefixes(pathname, OBSERVABILITY_PREFIXES);
            return (
              <NavLink
                key={link.to}
                to={link.to}
                className={() => navLinkClass({ isActive: active })}
              >
                {link.label}
              </NavLink>
            );
          }
          // Settings link should be active for /admin/settings and /admin/feature-flags paths
          if (link.label === 'Settings') {
            const active = isActiveForPrefixes(pathname, SETTINGS_PREFIXES);
            return (
              <NavLink
                key={link.to}
                to={link.to}
                className={() => navLinkClass({ isActive: active })}
              >
                {link.label}
              </NavLink>
            );
          }
          // Algorithm Management link should be active for both /admin/algorithm-management and /admin/formula-builder paths
          if (link.label === 'Algorithm Management') {
            const active = isActiveForPrefixes(pathname, ALGORITHM_MGMT_PREFIXES);
            return (
              <NavLink
                key={link.to}
                to={link.to}
                className={() => navLinkClass({ isActive: active })}
              >
                {link.label}
              </NavLink>
            );
          }
          if (link.label === 'AxisMatrix Approvals') {
            return (
              <NavLink
                key={link.to}
                to={link.to}
                className={navLinkClass}
              >
                <span className="flex items-center gap-2">
                  {link.label}
                  {pendingCount > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-ea-blue px-1.5 text-[11px] font-semibold text-white">
                      {pendingCount}
                    </span>
                  )}
                </span>
              </NavLink>
            );
          }
          return (
            <NavLink
              key={link.to}
              to={link.to}
              end={'end' in link ? link.end : undefined}
              className={navLinkClass}
            >
              {link.label}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
